/**
 * Bulk Upload Modal Component
 * Premium, enterprise-grade CSV upload with validation preview and order creation
 * 
 * UI/UX Polish Applied:
 * - Enhanced header hierarchy with subtext
 * - Hero upload zone with stronger visual emphasis
 * - Better visual grouping for required vs optional columns
 * - Informational callouts instead of alarming warnings
 * - First-class template download buttons
 * - Clear primary vs secondary CTAs
 */
import React, { useState, useCallback, useRef, useMemo } from 'react';
import { X, Upload, FileText, CheckCircle, XCircle, AlertTriangle, Loader2, Download, Info, FileSpreadsheet, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import './BulkUploadModal.css';

const API = process.env.REACT_APP_BACKEND_URL;

const BulkUploadModal = ({ isOpen, onClose, onSuccess, token }) => {
  const [step, setStep] = useState('upload'); // upload, preview, processing, complete
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [processResult, setProcessResult] = useState(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  // DUP-BYPASS (May 4, 2026) — required when skipDuplicates is OFF.
  // The backend rejects the request unless this is non-empty AND
  // the caller is Admin or Order Desk.
  const [bypassReason, setBypassReason] = useState('');
  const [tableFilter, setTableFilter] = useState('all'); // all, valid, invalid, duplicate
  const [rowPage, setRowPage] = useState(1);
  const [rowPageSize, setRowPageSize] = useState(50);
  const fileInputRef = useRef(null);

  // Handle file selection
  const handleFileSelect = useCallback((selectedFile) => {
    if (!selectedFile) return;
    
    if (!selectedFile.name.endsWith('.csv')) {
      setError('Only CSV files are supported');
      return;
    }
    
    if (selectedFile.size > 10 * 1024 * 1024) { // 10MB limit
      setError('File size must be less than 10MB');
      return;
    }
    
    setFile(selectedFile);
    setError(null);
  }, []);

  // Handle drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // Handle drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, [handleFileSelect]);

  // Validate CSV
  const validateFile = async () => {
    if (!file) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/api/bulk-upload/validate`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setValidationResult(response.data);
      setStep('preview');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to validate CSV file');
    } finally {
      setLoading(false);
    }
  };

  // Process validated rows
  const processUpload = async () => {
    if (!validationResult) return;
    
    setLoading(true);
    setStep('processing');
    setError(null);
    
    try {
      // Get current user info from token
      const userResponse = await axios.get(`${API}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const validRows = validationResult.rows.filter(r => r.is_valid);
      
      const response = await axios.post(
        `${API}/api/bulk-upload/process`,
        {
          file_name: validationResult.file_name,
          rows: validRows,
          skip_duplicates: skipDuplicates,
          bypass_reason: skipDuplicates ? null : bypassReason.trim(),
          created_by: userResponse.data.id,
          created_by_name: userResponse.data.full_name || userResponse.data.email
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      setProcessResult(response.data);
      setStep('complete');
      
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to process upload');
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  // Reset modal
  const resetModal = () => {
    setStep('upload');
    setFile(null);
    setValidationResult(null);
    setProcessResult(null);
    setError(null);
    setSkipDuplicates(true);
  };

  // Close modal
  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="bulk-upload-overlay" onClick={handleClose}>
      <div className="bulk-upload-modal" onClick={e => e.stopPropagation()}>
        {/* Enhanced Header */}
        <div className="bulk-upload-header">
          <div className="header-content">
            <div className="header-icon">
              <Upload size={18} />
            </div>
            <div className="header-text">
              <h2>Bulk Order Upload</h2>
              <p className="header-subtitle">Upload CSV files to create multiple orders at once</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="btn-close" onClick={handleClose} aria-label="Close modal">
            <X size={16} />
          </Button>
        </div>

        {/* Content */}
        <div className="bulk-upload-content">
          {error && (
            <div className="bulk-upload-error">
              <XCircle size={18} />
              {error}
            </div>
          )}

          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="upload-step-v2">
              {/* Split Layout: Left = Upload, Right = Info */}
              <div className="upload-split">
                {/* LEFT: Upload Zone (compact) */}
                <div className="upload-left">
                  <div 
                    className={`drop-zone drop-zone-compact ${dragActive ? 'drag-active' : ''} ${file ? 'has-file' : ''}`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="bulk-upload-dropzone"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={(e) => handleFileSelect(e.target.files[0])}
                      hidden
                    />
                    
                    {file ? (
                      <div className="file-selected file-selected-compact">
                        <FileText size={32} className="file-icon" />
                        <span className="file-name">{file.name}</span>
                        <span className="file-size">{(file.size / 1024).toFixed(1)} KB</span>
                        <span className="file-change-hint">Click to change</span>
                      </div>
                    ) : (
                      <div className="drop-content-compact">
                        <Upload size={28} className="upload-icon-compact" />
                        <p className="drop-text-compact">Drag & drop CSV or click to browse</p>
                        <span className="drop-meta-compact">CSV only · Max 1,000 rows</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* RIGHT: Info Panel */}
                <div className="upload-right">
                  {/* Collapsible Fields */}
                  <details className="fields-accordion" open>
                    <summary className="fields-summary">Required fields <span className="field-count">(8)</span></summary>
                    <div className="fields-list-compact">
                      <span className="field-chip req">Loan ID</span>
                      <span className="field-chip req">Task ID</span>
                      <span className="field-chip req">Contract Code</span>
                      <span className="field-chip req">Product Type</span>
                      <span className="field-chip req">Address</span>
                      <span className="field-chip req">City</span>
                      <span className="field-chip req">State</span>
                      <span className="field-chip req">County</span>
                    </div>
                  </details>
                  <details className="fields-accordion" open>
                    <summary className="fields-summary">Optional fields <span className="field-count">(9)</span></summary>
                    <div className="fields-list-compact">
                      <span className="field-chip opt" data-testid="bulk-upload-optional-field-zip">ZIP</span>
                      <span className="field-chip opt" data-testid="bulk-upload-optional-field-borrower">Borrower/Name</span>
                      <span className="field-chip opt" data-testid="bulk-upload-optional-field-seller">Seller Name(s)</span>
                      <span className="field-chip opt" data-testid="bulk-upload-optional-field-loan-amount">Loan Amount</span>
                      <span className="field-chip opt" data-testid="bulk-upload-optional-field-loan-date">Loan Date</span>
                      <span className="field-chip opt" data-testid="bulk-upload-optional-field-project-code">Project Code</span>
                      <span className="field-chip opt" data-testid="bulk-upload-optional-field-service">Service</span>
                      <span className="field-chip opt" data-testid="bulk-upload-optional-field-task-type">Task Type</span>
                      <span className="field-chip opt" data-testid="bulk-upload-optional-field-task-status">Task Status Name</span>
                    </div>
                    <p
                      className="fields-helper"
                      data-testid="bulk-upload-search-instructions-helper"
                    >
                      <strong>Project Code</strong>, <strong>Service</strong>, <strong>Task Type</strong>, and <strong>Task Status Name</strong> auto-populate the <em>Search Instructions</em> panel in Fulfillment.
                    </p>
                  </details>

                  {/* Inline Templates */}
                  <div className="templates-inline">
                    <span className="templates-label">Templates:</span>
                    <a 
                      href={`${API}/api/bulk-upload/template/empty`}
                      className="template-link"
                      download
                      data-testid="download-empty-template"
                      onClick={e => e.stopPropagation()}
                    >
                      <Download size={13} /> Empty
                    </a>
                    <span className="template-sep">·</span>
                    <a 
                      href={`${API}/api/bulk-upload/template/sample`}
                      className="template-link"
                      download
                      data-testid="download-sample-template"
                      onClick={e => e.stopPropagation()}
                    >
                      <Download size={13} /> With Sample
                    </a>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="upload-footer-v2">
                <Button variant="outline" onClick={handleClose}>Cancel</Button>
                <Button 
                  onClick={validateFile}
                  disabled={!file || loading}
                  isLoading={loading}
                  data-testid="validate-and-preview-btn"
                >
                  {loading ? (
                    'Validating...'
                  ) : (
                    <><CheckCircle size={16} /> Validate & Preview</>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Validation Summary & Decision */}
          {step === 'preview' && validationResult && (() => {
            const totalRows = validationResult.total_rows;
            const validRows = validationResult.valid_rows;
            const invalidRows = validationResult.invalid_rows;
            const dupRows = validationResult.duplicate_rows;
            // Phase A.7.8 — duplicates split by existing-order lifecycle state.
            // `dupBlocked` = active-state existing (skipped by default).
            // `dupAllowed` = terminal-state existing (AUTO-CREATED as new order).
            const dupBlocked = validationResult.duplicate_blocked_rows ?? 0;
            const dupAllowed = validationResult.duplicate_allowed_rows ?? 0;
            const warnRows = validationResult.warning_rows;
            const creatableSkippingDups = validationResult.validation_summary.total_creatable;
            // Phase A.7.8 — the "Skip duplicate rows" checkbox now only
            // affects ACTIVE-state duplicates. Terminal-state duplicates
            // are always auto-created (default business rule). When the
            // toggle is OFF, an admin can additionally bypass active dups.
            const creatable = skipDuplicates
              ? creatableSkippingDups
              : creatableSkippingDups + dupBlocked;
            const allDuplicates = dupRows === totalRows && invalidRows === 0;
            const noneCreatable = creatable === 0;
            const hasMix = dupRows > 0 && (creatableSkippingDups > 0 || (!skipDuplicates && dupBlocked > 0));
            // Reason is only required when admin tries to override the
            // remaining ACTIVE-state duplicates (terminal ones already
            // auto-allowed without a reason).
            const reasonRequired = !skipDuplicates && dupBlocked > 0;
            const reasonValid = bypassReason.trim().length >= 5;
            const blockedByReason = reasonRequired && !reasonValid;

            // Filter rows for the table
            const filteredRows = validationResult.rows.filter(row => {
              if (tableFilter === 'valid') return row.is_valid && !row.is_duplicate;
              if (tableFilter === 'invalid') return !row.is_valid;
              if (tableFilter === 'duplicate') return row.is_duplicate;
              if (tableFilter === 'allowed') return row.duplicate_status === 'allowed_terminal';
              if (tableFilter === 'blocked') return row.duplicate_status === 'blocked_active';
              return true;
            });

            // Pagination
            const totalFiltered = filteredRows.length;
            const totalPages = Math.ceil(totalFiltered / rowPageSize);
            const safePage = Math.min(rowPage, totalPages || 1);
            const startIdx = (safePage - 1) * rowPageSize;
            const endIdx = Math.min(startIdx + rowPageSize, totalFiltered);
            const pageRows = filteredRows.slice(startIdx, endIdx);

            return (
            <div className="preview-step-v2" data-testid="bulk-upload-preview">
              {/* Stepper */}
              <div className="bulk-stepper">
                <div className="step-item completed">
                  <div className="step-circle"><CheckCircle size={16} /></div>
                  <span>Upload</span>
                </div>
                <div className="step-connector completed"></div>
                <div className="step-item active">
                  <div className="step-circle">2</div>
                  <span>Review</span>
                </div>
                <div className="step-connector"></div>
                <div className="step-item">
                  <div className="step-circle">3</div>
                  <span>Create</span>
                </div>
              </div>

              {/* Decision Banner — THE MOST IMPORTANT ELEMENT */}
              <div className={`decision-banner ${noneCreatable ? 'banner-blocked' : (dupBlocked > 0 && !skipDuplicates === false) ? 'banner-partial' : hasMix ? 'banner-partial' : 'banner-ready'}`} data-testid="decision-banner">
                {/* Case A: All rows are duplicates AND all are blocked (active existing) */}
                {allDuplicates && dupAllowed === 0 ? (
                  <>
                    <div className="banner-icon"><AlertTriangle size={24} /></div>
                    <div className="banner-content">
                      <strong>All {totalRows} rows match active in-progress orders.</strong>
                      <span>No new orders will be created. Every row matched an in-flight existing order — these are blocked to prevent duplicate work.</span>
                    </div>
                  </>
                ) : noneCreatable ? (
                  <>
                    <div className="banner-icon"><XCircle size={24} /></div>
                    <div className="banner-content">
                      <strong>No orders can be created.</strong>
                      <span>{invalidRows > 0 ? `${invalidRows} row(s) have validation errors.` : ''} {dupBlocked > 0 ? `${dupBlocked} row(s) match active in-progress orders (blocked).` : ''} Please fix the issues and re-upload.</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="banner-icon"><Info size={24} /></div>
                    <div className="banner-content">
                      <strong>{creatable} new order{creatable !== 1 ? 's' : ''} will be created.</strong>
                      <span>
                        {validRows > 0 && `${validRows} brand-new row${validRows !== 1 ? 's' : ''}.`}
                        {dupAllowed > 0 && (
                          ` ${dupAllowed} duplicate row${dupAllowed !== 1 ? 's' : ''} will be recreated (existing order is complete / cancelled).`
                        )}
                        {dupBlocked > 0 && skipDuplicates && (
                          ` ${dupBlocked} duplicate row${dupBlocked !== 1 ? 's' : ''} will be skipped (existing order is still active).`
                        )}
                        {dupBlocked > 0 && !skipDuplicates && (
                          ` ${dupBlocked} active duplicate${dupBlocked !== 1 ? 's' : ''} will be force-created via admin bypass.`
                        )}
                        {invalidRows > 0 ? ` ${invalidRows} invalid row(s) excluded.` : ''}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Summary Cards */}
              <div className="validation-summary-v2">
                <div className="summary-card-v2 total" data-testid="summary-total">
                  <span className="summary-val">{totalRows}</span>
                  <span className="summary-lbl">Total Rows</span>
                </div>
                <div className="summary-card-v2 valid" data-testid="summary-valid">
                  <CheckCircle size={16} />
                  <span className="summary-val">{validRows}</span>
                  <span className="summary-lbl">New (Valid)</span>
                </div>
                {dupAllowed > 0 && (
                  <div
                    className="summary-card-v2 allowed has-info"
                    data-testid="summary-allowed"
                    style={{ borderTopColor: '#10b981' }}
                    title="These rows are duplicates of orders that are already complete or cancelled. A fresh order will be created for each, and every auto-allow is recorded in the audit log."
                  >
                    <Info
                      size={12}
                      className="card-info-icon"
                      style={{ color: '#059669', cursor: 'help' }}
                      data-testid="allowed-info-icon"
                      aria-label="Why are these rows allowed?"
                    />
                    <CheckCircle size={16} style={{ color: '#10b981' }} />
                    <span className="summary-val" style={{ color: '#065f46' }}>{dupAllowed}</span>
                    <span className="summary-lbl">Allowed (Existing Complete/Cancelled)</span>
                  </div>
                )}
                {dupBlocked > 0 && (
                  <div className="summary-card-v2 dup" data-testid="summary-duplicates-blocked">
                    <AlertTriangle size={16} />
                    <span className="summary-val">{dupBlocked}</span>
                    <span className="summary-lbl">Blocked (Existing Active)</span>
                  </div>
                )}
                <div className="summary-card-v2 invalid" data-testid="summary-invalid">
                  <XCircle size={16} />
                  <span className="summary-val">{invalidRows}</span>
                  <span className="summary-lbl">Invalid</span>
                </div>
                {warnRows > 0 && (
                  <div className="summary-card-v2 warn" data-testid="summary-warnings">
                    <AlertTriangle size={16} />
                    <span className="summary-val">{warnRows}</span>
                    <span className="summary-lbl">Warnings</span>
                  </div>
                )}
              </div>

              {/* Phase A.7.8 simplification (Feb 2026) — the Result line
                  and the green auto-allow explanation banner were removed
                  per user feedback. The same information is already
                  conveyed by the header decision banner + the summary
                  cards (including the new "Allowed" card with a hover
                  info-icon tooltip). Removing them eliminates duplicated
                  copy and lets Order Desk users grasp the outcome in one
                  read. */}

              {/* Skip Active Duplicates Toggle — only shown when there are ACTIVE dups,
                  since terminal dups always auto-allow and don't need a toggle. */}
              {dupBlocked > 0 && (
                <div className="dup-toggle" data-testid="skip-duplicates-toggle">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={skipDuplicates}
                      onChange={(e) => {
                        setSkipDuplicates(e.target.checked);
                        if (e.target.checked) setBypassReason('');
                      }}
                      data-testid="skip-duplicates-checkbox"
                    />
                    <span className="toggle-text">
                      Skip {dupBlocked} active-state duplicate row{dupBlocked !== 1 ? 's' : ''}
                    </span>
                  </label>
                  <span className="toggle-hint">
                    {skipDuplicates
                      ? `${dupBlocked} row${dupBlocked !== 1 ? 's' : ''} will not be created (existing order is still active / in-progress)`
                      : 'Force-create active duplicates as new orders (admin override). A reason is required.'}
                  </span>

                  {/* DUP-BYPASS (May 4, 2026) — required reason input
                      shown only when toggle is OFF. Backend rejects the
                      request if reason is empty / <5 chars OR if the
                      caller is not Admin / Order Desk. Phase A.7.8: this
                      now ONLY controls active-state dups; terminal-state
                      dups auto-allow without needing a reason. */}
                  {!skipDuplicates && (
                    <div className="bypass-warning-block" data-testid="bypass-warning-block" style={{
                      marginTop: '12px',
                      padding: '12px',
                      background: '#fef3c7',
                      border: '1px solid #f59e0b',
                      borderRadius: '6px',
                    }}>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <AlertTriangle size={16} color="#b45309" style={{ flexShrink: 0, marginTop: '2px' }} />
                        <div style={{ fontSize: '12px', color: '#78350f', lineHeight: 1.5 }}>
                          <strong>Admin override active.</strong> {dupBlocked} duplicate row{dupBlocked !== 1 ? 's' : ''} whose existing order is still
                          <code style={{ background: '#fde68a', padding: '0 4px', borderRadius: '3px', margin: '0 4px' }}>active / in-progress</code>
                          will be force-created. This bypasses the standard guard against duplicate active work. Every override is recorded in the audit log.
                        </div>
                      </div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#78350f', marginBottom: '4px' }}>
                        Reason for force-creating active duplicates <span style={{ color: '#b91c1c' }}>*</span>
                      </label>
                      <input
                        type="text"
                        value={bypassReason}
                        onChange={(e) => setBypassReason(e.target.value)}
                        placeholder="e.g., Client requested re-issue despite active order; supersedes prior request"
                        maxLength={500}
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          fontSize: '12px',
                          border: `1px solid ${reasonValid ? '#10b981' : '#f59e0b'}`,
                          borderRadius: '4px',
                          background: 'white',
                          fontFamily: 'inherit',
                        }}
                        data-testid="bypass-reason-input"
                      />
                      <div style={{ marginTop: '4px', fontSize: '10px', color: '#92400e' }}>
                        {bypassReason.trim().length === 0
                          ? 'Required (minimum 5 characters).'
                          : bypassReason.trim().length < 5
                          ? `Need ${5 - bypassReason.trim().length} more character${5 - bypassReason.trim().length !== 1 ? 's' : ''}.`
                          : `${bypassReason.trim().length} character${bypassReason.trim().length !== 1 ? 's' : ''} — reason will be logged.`}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Column Mapping (compact) */}
              <details className="mapping-details">
                <summary className="mapping-summary">
                  <span>Column Mapping ({Object.keys(validationResult.column_mapping).length} fields matched)</span>
                </summary>
                <div className="mapping-tags-v2">
                  {Object.entries(validationResult.column_mapping).map(([orig, mapped]) => (
                    <span key={orig} className="mapping-tag-v2">
                      {orig} <span className="map-arrow">→</span> <strong>{mapped}</strong>
                    </span>
                  ))}
                </div>
                {validationResult.unrecognized_columns.length > 0 && (
                  <div className="unrecognized-warning">
                    <AlertTriangle size={14} />
                    Unrecognized columns (ignored): {validationResult.unrecognized_columns.join(', ')}
                  </div>
                )}
              </details>

              {/* Data Table — Secondary inspection */}
              <div className="table-section">
                <div className="table-header-bar">
                  <h4 className="table-title">Row Details</h4>
                  <div className="table-filters" data-testid="table-filters">
                    <button 
                      className={`filter-tab ${tableFilter === 'all' ? 'active' : ''}`}
                      onClick={() => { setTableFilter('all'); setRowPage(1); }}
                    >All ({totalRows})</button>
                    <button 
                      className={`filter-tab ${tableFilter === 'valid' ? 'active' : ''}`}
                      onClick={() => { setTableFilter('valid'); setRowPage(1); }}
                    >New ({validRows})</button>
                    {dupAllowed > 0 && (
                      <button
                        className={`filter-tab ${tableFilter === 'allowed' ? 'active' : ''}`}
                        onClick={() => { setTableFilter('allowed'); setRowPage(1); }}
                        style={tableFilter === 'allowed' ? { background: '#10b981', borderColor: '#10b981', color: 'white' } : {}}
                      >Allowed ({dupAllowed})</button>
                    )}
                    {dupBlocked > 0 && (
                      <button
                        className={`filter-tab ${tableFilter === 'blocked' ? 'active' : ''}`}
                        onClick={() => { setTableFilter('blocked'); setRowPage(1); }}
                      >Blocked ({dupBlocked})</button>
                    )}
                    {invalidRows > 0 && (
                      <button 
                        className={`filter-tab ${tableFilter === 'invalid' ? 'active' : ''}`}
                        onClick={() => { setTableFilter('invalid'); setRowPage(1); }}
                      >Invalid ({invalidRows})</button>
                    )}
                  </div>
                </div>
                <div className="preview-table-container-v2">
                  <table className="preview-table">
                    <thead className="tf-thead">
                      <tr>
                        <th>Row</th>
                        <th>Status</th>
                        <th>Loan ID</th>
                        <th>Task ID</th>
                        <th>Contract</th>
                        <th>Product Type</th>
                        <th>Borrower</th>
                        <th>Seller</th>
                        <th>Address</th>
                        <th>State</th>
                        <th>Issues</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageRows.map((row) => (
                        <tr 
                          key={row.row_number}
                          className={`
                            ${!row.is_valid ? 'row-invalid' : ''}
                            ${row.is_duplicate ? 'row-duplicate' : ''}
                            ${row.warnings?.length > 0 && row.is_valid ? 'row-warning' : ''}
                          `}
                        >
                          <td>{row.row_number}</td>
                          <td>
                            {!row.is_valid ? (
                              <span className="status-badge invalid">
                                <XCircle size={14} /> Invalid
                              </span>
                            ) : row.duplicate_status === 'allowed_terminal' ? (
                              <span
                                className="status-badge duplicate-allowed"
                                style={{ background: '#d1fae5', color: '#065f46', borderColor: '#10b981' }}
                                data-testid={`row-status-allowed-${row.row_number}`}
                              >
                                <CheckCircle size={14} /> Duplicate (Allowed)
                              </span>
                            ) : row.is_duplicate ? (
                              <span
                                className="status-badge duplicate"
                                data-testid={`row-status-blocked-${row.row_number}`}
                              >
                                <AlertTriangle size={14} /> Duplicate (Blocked)
                              </span>
                            ) : row.warnings?.length > 0 ? (
                              <span className="status-badge warning">
                                <AlertTriangle size={14} /> Warning
                              </span>
                            ) : (
                              <span className="status-badge valid">
                                <CheckCircle size={14} /> Valid
                              </span>
                            )}
                          </td>
                          <td>{row.loan_id || '-'}</td>
                          <td>{row.task_id || '-'}</td>
                          <td>
                            {row.mapped_client_code ? (
                              <span className="mapped-value">{row.mapped_client_code}</span>
                            ) : (
                              <span className="unmapped-value">{row.contract_code || '-'}</span>
                            )}
                          </td>
                          <td>
                            {row.mapped_product ? (
                              <span className="product-badge">{row.mapped_product}</span>
                            ) : (
                              <span className="unmapped-value">{row.product_type || 'Missing'}</span>
                            )}
                          </td>
                          <td>{row.borrower_name || '-'}</td>
                          <td data-testid={`bulk-preview-seller-${row.row_number}`}>{row.seller_name || '-'}</td>
                          <td className="address-cell">{row.property_address || '-'}</td>
                          <td>{row.state || '-'}</td>
                          <td className="issues-cell">
                            {row.validation_errors?.length > 0 && (
                              <span className="error-text">{row.validation_errors.join('; ')}</span>
                            )}
                            {row.warnings?.length > 0 && (
                              <span className="warning-text">{row.warnings.join('; ')}</span>
                            )}
                            {row.is_duplicate && row.existing_order_number && (
                              <span
                                className="duplicate-text"
                                style={row.duplicate_status === 'allowed_terminal' ? { color: '#065f46' } : undefined}
                              >
                                Exists: {row.existing_order_number}
                                {row.existing_status && ` (${row.existing_status})`}
                                {row.duplicate_status === 'allowed_terminal' && ' — will recreate'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {totalFiltered > 0 && (
                    <div className="table-pagination" data-testid="row-pagination">
                      <div className="pagination-info">
                        Showing {startIdx + 1}–{endIdx} of {totalFiltered} rows
                      </div>
                      <div className="pagination-controls">
                        <select
                          value={rowPageSize}
                          onChange={(e) => { setRowPageSize(Number(e.target.value)); setRowPage(1); }}
                          className="page-size-select"
                          data-testid="page-size-select"
                        >
                          <option value={50}>50 / page</option>
                          <option value={100}>100 / page</option>
                          <option value={200}>200 / page</option>
                        </select>
                        <button
                          className="pagination-btn"
                          disabled={safePage <= 1}
                          onClick={() => setRowPage(safePage - 1)}
                          data-testid="prev-page-btn"
                        >
                          <ChevronLeft size={16} /> Prev
                        </button>
                        <span className="page-indicator">
                          Page {safePage} of {totalPages}
                        </span>
                        <button
                          className="pagination-btn"
                          disabled={safePage >= totalPages}
                          onClick={() => setRowPage(safePage + 1)}
                          data-testid="next-page-btn"
                        >
                          Next <ChevronRight size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                  {filteredRows.length === 0 && (
                    <p className="table-note">No rows match the selected filter</p>
                  )}
                </div>
              </div>

              {/* Action Bar — Fixed at bottom */}
              <div className="action-bar-v2" data-testid="action-bar">
                <Button variant="outline" onClick={resetModal}>
                  ← Back to Upload
                </Button>
                <div className="action-right">
                  {noneCreatable && (
                    <span className="action-disabled-reason" data-testid="disabled-reason">
                      {allDuplicates && dupAllowed === 0
                        ? 'All rows match active in-progress orders — nothing to create'
                        : 'No valid rows available for order creation'}
                    </span>
                  )}
                  {blockedByReason && !noneCreatable && (
                    <span className="action-disabled-reason" data-testid="disabled-reason-bypass" style={{ color: '#b45309' }}>
                      Enter a reason (≥5 chars) to force-create active duplicates
                    </span>
                  )}
                  <Button 
                    onClick={processUpload}
                    disabled={noneCreatable || loading || blockedByReason}
                    isLoading={loading}
                    data-testid="create-orders-btn"
                  >
                    {loading ? (
                      'Processing...'
                    ) : noneCreatable ? (
                      '0 Orders to Create'
                    ) : (
                      `Create ${creatable} Order${creatable !== 1 ? 's' : ''}`
                    )}
                  </Button>
                </div>
              </div>
            </div>
            );
          })()}

          {/* Step 3: Processing */}
          {step === 'processing' && (
            <div className="processing-step">
              <div className="processing-spinner">
                <Loader2 size={56} className="spin" />
              </div>
              <h3>Creating Orders...</h3>
              <p>Please wait while we process your upload.</p>
            </div>
          )}

          {/* Step 4: Complete */}
          {step === 'complete' && processResult && (() => {
            const createdCount = processResult.created_count;
            const skippedCount = processResult.skipped_count;
            const failedCount = processResult.failed_count;
            const autoAllowedCount = processResult.auto_allowed_count ?? 0;
            const bypassedCount = processResult.bypassed_count ?? 0;

            // Phase A.7.8 — contextual headline reflects outcome.
            let headline = 'Upload Complete!';
            let headlineColor = undefined;
            if (createdCount === 0 && skippedCount > 0 && failedCount === 0) {
              headline = 'No new orders created — all rows matched existing active orders';
              headlineColor = '#b45309';
            } else if (createdCount > 0 && failedCount > 0) {
              headline = `${createdCount} order${createdCount !== 1 ? 's' : ''} created, ${failedCount} failed`;
              headlineColor = '#b45309';
            } else if (failedCount > 0 && createdCount === 0) {
              headline = `Upload Failed — ${failedCount} row${failedCount !== 1 ? 's' : ''} could not be processed`;
              headlineColor = '#b91c1c';
            } else if (createdCount > 0) {
              headline = `${createdCount} order${createdCount !== 1 ? 's' : ''} created successfully`;
            }

            return (
            <div className="complete-step">
              <div className="complete-icon">
                <CheckCircle size={56} />
              </div>
              <h3 data-testid="complete-headline" style={headlineColor ? { color: headlineColor } : undefined}>
                {headline}
              </h3>

              <div className="result-summary">
                <div className="result-item created">
                  <span className="result-value">{createdCount}</span>
                  <span className="result-label">Orders Created</span>
                </div>
                {autoAllowedCount > 0 && (
                  <div
                    className="result-item auto-allowed"
                    data-testid="result-item-auto-allowed"
                    style={{ borderTopColor: '#10b981' }}
                  >
                    <span className="result-value" style={{ color: '#065f46' }}>
                      {autoAllowedCount}
                    </span>
                    <span className="result-label">Recreated (Existing Complete/Cancelled)</span>
                  </div>
                )}
                <div className="result-item skipped">
                  <span className="result-value">{skippedCount}</span>
                  <span className="result-label">Skipped (Active Duplicates)</span>
                </div>
                <div className="result-item failed">
                  <span className="result-value">{failedCount}</span>
                  <span className="result-label">Failed</span>
                </div>
                {bypassedCount > 0 && (
                  <div
                    className="result-item bypassed"
                    data-testid="result-item-bypassed"
                    style={{ borderTopColor: '#f59e0b' }}
                  >
                    <span className="result-value" style={{ color: '#b45309' }}>
                      {bypassedCount}
                    </span>
                    <span className="result-label">Force-Created (Admin Bypass)</span>
                  </div>
                )}
              </div>

              {processResult.created_orders?.length > 0 && (
                <div className="created-orders-preview">
                  <h4>Created Orders</h4>
                  <div className="orders-list">
                    {processResult.created_orders.slice(0, 10).map((order) => (
                      <div key={order.order_id} className="order-item">
                        <span className="order-number">{order.order_number}</span>
                        <span className="order-loan">Loan: {order.loan_id}</span>
                      </div>
                    ))}
                    {processResult.created_orders.length > 10 && (
                      <p className="more-orders">
                        + {processResult.created_orders.length - 10} more orders
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="complete-actions">
                <Button variant="outline" onClick={resetModal}>
                  Upload Another File
                </Button>
                <Button onClick={handleClose}>
                  Done
                </Button>
              </div>
            </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
